/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { TouchableOpacity, View } from "react-native";

import { Button, Text } from "UpduoLibrary";
import { FilterSet } from "components/DashboardRoute/types";
import { entriesOf, isNotNil, useWindowDimensions } from "config/Utilities";
import {
  skaleModalTextGray,
  skaleWhite,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import Measure from "./Measure";
import FilterTag from "./FilterTag";
import FilterModal from "../FilterModal";

interface FilterTagRowProps {
  filterSet: FilterSet;
  setFilterSet: React.Dispatch<React.SetStateAction<FilterSet>>;
}

export default function FilterTagRow({
  filterSet,
  setFilterSet,
}: FilterTagRowProps) {
  const filterNames: { [K in keyof Required<FilterSet>]: [string, string] } = {
    complete: ["Complete Sessions", "Incomplete Sessions"],
    withVideo: ["With Video", "Without Video"],
    withTranscript: ["With Transcript", "Without Transcript"],
  };

  // TODO we shouldn't compare the filter row size with an arbitrary fraction of the
  // widnow width. Instead, we should measure how much space the tags actually take
  // up vs how much space is available to see if they overflow
  const { width: windowWidth } = useWindowDimensions();
  const maxFilterTagRowWidth = Math.round(0.17 * windowWidth);

  const [currFilterTagRowWidth, setCurrFilterTagRowWidth] = useState(0);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  const closeFilterModal = () => {
    setIsFilterModalVisible(false);
  };
  const toggleFilterModalVisible = () => {
    setIsFilterModalVisible((old) => !old);
  };
  const _onApplyFilters = (newFilters: FilterSet) => {
    setFilterSet(newFilters);
    closeFilterModal();
  };

  const updateFilter = (
    filter: keyof FilterSet,
    value: boolean | undefined
  ) => {
    setFilterSet((old) => ({ ...old, [filter]: value }));
  };

  const collapseFilterTagRow = currFilterTagRowWidth > maxFilterTagRowWidth;
  const numActiveSessionFilters =
    Object.values(filterSet).filter(isNotNil).length;

  const ActivityFilterText =
    collapseFilterTagRow && numActiveSessionFilters > 0 ? (
      <View style={{ flexDirection: "row" }}>
        <Text
          style={{
            color: upduoPrimaryBlack,
            fontStyle: "normal",
            fontWeight: "500",
            fontSize: 16,
            marginRight: 4,
          }}
        >
          {"Filter"}
        </Text>
        <TouchableOpacity
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            backgroundColor: "#E4F0F9",
            marginLeft: 4,
            justifyContent: "center",
            alignItems: "center",
          }}
          onPress={toggleFilterModalVisible}
        >
          <Text
            style={{
              color: "#367CF6",
              fontStyle: "normal",
              fontWeight: "700",
              fontSize: 14,
            }}
          >
            {numActiveSessionFilters}
          </Text>
        </TouchableOpacity>
      </View>
    ) : (
      <Text
        style={{
          color: upduoPrimaryBlack,
          fontStyle: "normal",
          fontWeight: "500",
          fontSize: 16,
        }}
      >
        {"+ Add Filter"}
      </Text>
    );

  return (
    <>
      <FilterModal
        isVisible={isFilterModalVisible}
        onClose={closeFilterModal}
        onApply={_onApplyFilters}
        initialFilterSet={filterSet}
      />

      <View>
        {!collapseFilterTagRow ? (
          <Measure onMeasure={(width) => setCurrFilterTagRowWidth(width)}>
            <View style={{ display: "flex", flexDirection: "row" }}>
              {entriesOf(filterSet)
                .filter(([, value]) => isNotNil(value))
                .map(([key, value]) => (
                  <FilterTag
                    text={filterNames[key][value ? 0 : 1]}
                    onPress={() => updateFilter(key, undefined)}
                    key={key}
                  />
                ))}
            </View>
          </Measure>
        ) : null}
      </View>
      <Button
        marginRight={17}
        onPress={toggleFilterModalVisible}
        height={34}
        width={115}
        borderColor={skaleModalTextGray}
        borderWidth={1}
        borderRadius={5}
        color={skaleWhite}
        content={ActivityFilterText}
      />
    </>
  );
}
